import { IValidationTypes, SubmitMapType, ValidationItem, ValidationResponse } from './types';

export enum USERNAME {
  MIN_CHARACTERS = 'min_characters',
  MAX_CHARACTERS = 'max_characters',
  ONLY_ASCII = 'allow_ascii_characters',
  SPEC_SYMBOL = 'user_numerals_spec_symbol'
}

export enum PASSWORD {
  MIN_CHARACTERS = 'min_characters',
  LETTER_CASE = 'letter_case',
  NUMERALS = 'numerals',
  SPEC_CHARACTERS = 'spec_characters',
  ONLY_ASCII = 'allow_ascii_characters',
  PERSONAL_DATA = 'exclude_personal_data',
  DICTIONARY = 'password_dictionary'
}

export const validationTypes: IValidationTypes = {
  [PASSWORD.MIN_CHARACTERS]: {
    regExp: '.{number,}',
    componentType: 'input'
  },
  [USERNAME.MAX_CHARACTERS]: {
    regExp: '^.{1,number}$',
    componentType: 'input'
  },
  [PASSWORD.LETTER_CASE]: {
    regExp: '[a-z].*[A-Z]|[A-Z].*[a-z]',
    componentType: 'checkbox'
  },
  [PASSWORD.NUMERALS]: {
    regExp: '[0-9]',
    componentType: 'checkbox'
  },
  [USERNAME.SPEC_SYMBOL]: {
    regExp: '[0-9_-]',
    componentType: 'checkbox'
  },
  [PASSWORD.SPEC_CHARACTERS]: {
    regExp: '',
    componentType: 'select'
  },
  [PASSWORD.ONLY_ASCII]: {
    regExp: '^[\x00-\xFF]+$',
    componentType: 'checkbox'
  },
  [PASSWORD.PERSONAL_DATA]: {
    regExp: '[0-9_-]',
    componentType: 'extraData'
  },
  [PASSWORD.DICTIONARY]: {
    regExp: '[0-9_-]',
    componentType: 'checkbox'
  }
};

export enum ValidationFieldName {
  PASSWORD_FIELD = 'password',
  USERNAME_FIELD = 'username'
}

export enum protectStatuses {
  ABSENT = 'absent',
  STRONG = 'strong',
  GOOD = 'good',
  WEAK = 'weak'
}

export const personalData = [
  "user's name",
  'nickname',
  'user_metadata.name',
  'user_metadata.first',
  'user_metadata.last'
];

export const specialCharacters: Record<string, RegExp> = {
  '#$%&@^`~': /[#$%&@^`~]/,
  '.,:;': /[.,:;]/,
  '"\'': /["']/,
  '\\/|_-': /[\\|/_-]/,
  '<>*+!?=': /[<>*+!?=]/,
  '[ { ( ) } ]': /[{([\])}]/
};

export const FieldValidationMap = {
  username: {
    hasProtectStatus: false,
    validationRule: [
      USERNAME.MIN_CHARACTERS,
      USERNAME.MAX_CHARACTERS,
      USERNAME.ONLY_ASCII,
      USERNAME.SPEC_SYMBOL
    ]
  },
  password: {
    hasProtectStatus: true,
    validationRule: [
      PASSWORD.MIN_CHARACTERS,
      PASSWORD.LETTER_CASE,
      PASSWORD.NUMERALS,
      PASSWORD.SPEC_CHARACTERS,
      PASSWORD.ONLY_ASCII,
      PASSWORD.PERSONAL_DATA,
      PASSWORD.DICTIONARY
    ]
  }
};

export const mapFormToFields = ({
  fields_validation,
  validation_spec_symbol,
  validation_min_length,
  validation_max_length
}: SubmitMapType): ValidationItem => {
  const values: ValidationItem = Object.entries(fields_validation)
    .filter(
      ([key, field]) =>
        !!field &&
        key !== PASSWORD.SPEC_CHARACTERS &&
        key !== PASSWORD.MIN_CHARACTERS &&
        key !== USERNAME.MAX_CHARACTERS
    )
    .reduce(
      (prev, [key]) => ({
        ...prev,
        settings_state: { ...prev.settings_state, [key]: validationTypes[key].regExp },
        regexps: [...prev.regexps, validationTypes[key].regExp]
      }),
      {
        settings_state: {},
        regexps: []
      }
    );

  if (fields_validation.min_characters) {
    const reg = validationTypes.min_characters.regExp.replace(
      'number',
      validation_min_length.toString()
    );

    values.regexps.push(reg);
    values.settings_state.min_characters = reg;
  }

  if (fields_validation.max_characters && validation_max_length) {
    const reg = validationTypes.max_characters.regExp.replace(
      'number',
      validation_max_length.toString()
    );

    values.regexps.push(reg);
    values.settings_state.max_characters = reg;
  }

  if (fields_validation.spec_characters && validation_spec_symbol) {
    const flatSpecSymbols = validation_spec_symbol
      .map((val) => specialCharacters[val].source)
      .reduce((prev, curr) => {
        return `${prev}|${curr}`;
      });

    values.regexps.push(flatSpecSymbols);
    values.settings_state.spec_characters = flatSpecSymbols;
  }

  return values;
};

export const getMinLengthFromRegExp = (
  fieldsValidation: ValidationResponse,
  name: string
): number => {
  const regExp = fieldsValidation.find((field) => field.name === name)?.settings_state
    .min_characters;
  if (regExp && typeof regExp === 'string') {
    const searchDigits = regExp.match(new RegExp('\\d', 'g')) || ['7'];
    return Number(searchDigits.reduce((prev, curr) => prev + curr));
  }

  return 7;
};

export const getMaxLengthFromRegExp = (
  fieldsValidation: ValidationResponse,
  name: string
): number => {
  const regExp = fieldsValidation.find((field) => field.name === name)?.settings_state
    .max_characters;
  if (regExp && typeof regExp === 'string') {
    const searchDigits = regExp.match(new RegExp('\\d', 'g')) || ['25'];
    return Number(searchDigits.reduce((prev, curr, index) => (index > 0 ? prev + curr : prev), 0));
  }

  return 25;
};

export const mapSpecialCharacters = (fieldsValidation: ValidationResponse): string[] => {
  const regExp = fieldsValidation.find((field) => field.name === ValidationFieldName.PASSWORD_FIELD)
    ?.settings_state.spec_characters;

  if (regExp && typeof regExp === 'string') {
    if (regExp.includes(']|[')) {
      return regExp
        .split(']|[')
        .map((value, index, array) => {
          if (index === 0) {
            return `${value}]`;
          }
          if (index === array.length - 1) {
            return `[${value}`;
          }
          return `[${value}]`;
        })
        .map(
          (value) =>
            Object.keys(specialCharacters).find((el) => specialCharacters[el].source === value) ||
            ''
        );
    }
    return [
      Object.keys(specialCharacters).find((el) => specialCharacters[el].source === regExp) || ''
    ];
  }

  return [];
};
